// src/pages/KentyouAggregations.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import { fetchAggregations, selectAggregations } from "../slices/aggregationsSlice";
import "../design/KentyouAggregations.css";
import { Breadcrumb } from "antd";
import { Link } from "react-router-dom";


ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

export default function KentyouAggregations() {
  const dispatch = useDispatch();
  const {
    status,
    error,
    snapshotTime,
    deviceCounts,
    stepsStats,
    heartRateStats,
    oxymetryStats,
    batteryStats,
  } = useSelector(selectAggregations);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchAggregations());
    }
  }, [dispatch, status]);

  const formattedSnapshot = snapshotTime
    ? new Date(snapshotTime).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  // ---------- Helpers to build chart configs ----------

  const buildDeviceChartData = () => {
    if (!deviceCounts) return null;
    const { gateways = 0, watches = 0, noah = 0 } = deviceCounts;
    const others =
      (deviceCounts.total || 0) - gateways - watches - noah;

    return {
      labels: ["Gateways", "Watches", "NOAH", "Others"],
      datasets: [
        {
          data: [gateways, watches, noah, others > 0 ? others : 0],
          backgroundColor: ["#0f766e", "#38bdf8", "#6366f1", "#9ca3af"],
          borderWidth: 0,
        },
      ],
    };
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
  };

  const buildTripleBarChart = (stats, label, color = "#0f766e") => {
    if (!stats || stats.count === 0 || stats.avg == null) return null;
    return {
      data: {
        labels: ["Avg", "Min", "Max"],
        datasets: [
          {
            label,
            data: [stats.avg, stats.min, stats.max],
            backgroundColor: [color, `${color}cc`, `${color}99`],
            borderRadius: 6,
            barThickness: 24,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 } },
          },
          y: {
            grid: { color: "#e5e7eb" },
            ticks: { font: { size: 11 } },
          },
        },
      },
    };
  };

  const buildBatteryDistributionChart = () => {
    if (!batteryStats) return null;
    const { lowBatteryCount = 0, midBatteryCount = 0, highBatteryCount = 0 } =
      batteryStats;
    return {
      data: {
        labels: ["Low", "Medium", "High"],
        datasets: [
          {
            label: "Number of watches",
            data: [lowBatteryCount, midBatteryCount, highBatteryCount],
            backgroundColor: ["#f97316", "#facc15", "#22c55e"],
            borderRadius: 6,
            barThickness: 28,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 11 } } },
          y: { grid: { color: "#e5e7eb" }, ticks: { font: { size: 11 } } },
        },
      },
    };
  };

  const deviceChartData = buildDeviceChartData();
  const stepsChart = buildTripleBarChart(stepsStats, "Steps", "#0f766e");
  const heartChart = buildTripleBarChart(
    heartRateStats,
    "Heart rate (bpm)",
    "#38bdf8"
  );
  const oxymChart = buildTripleBarChart(
    oxymetryStats,
    "SpO₂ (%)",
    "#6366f1"
  );
  const batteryChart = buildTripleBarChart(
    batteryStats,
    "Battery (%)",
    "#22c55e"
  );
  const batteryDistChart = buildBatteryDistributionChart();

  // ---------- JSX ----------

  return (
    <>
    <div className="page-header">
        <div className="page-breadcrumb-wrapper">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">Home</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/kentyouhome">KentyouEye Data Visualisations</Link></Breadcrumb.Item>
            <Breadcrumb.Item>Metrics</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>
    <div className="ka-root">
      <header className="ka-header">
        <div className="ka-title">KentyouEye Dashboard</div>
        {formattedSnapshot && (
          <div className="ka-subtitle">Last updated: {formattedSnapshot}</div>
        )}
      </header>

      {status === "loading" && (
        <div className="ka-loading">Computing aggregations…</div>
      )}

      {status === "failed" && (
        <div className="ka-error">Failed to load aggregations: {error}</div>
      )}

      {status === "succeeded" && (
        <>
          {/* SECTION 1 – Devices overview */}
          <section className="ka-section">
            <h2 className="ka-section-title">Devices overview</h2>
            <div className="ka-card-row">
              <div className="ka-card">
                <div className="ka-card-header">
                  <span className="ka-card-title">Devices by type</span>
                  <span className="ka-chip">Snapshot</span>
                </div>
                <div className="ka-chart">
                  {deviceChartData ? (
                    <Doughnut data={deviceChartData} options={doughnutOptions} />
                  ) : (
                    <div className="ka-empty">No device data available.</div>
                  )}
                </div>
                {deviceCounts && (
                  <div className="ka-legend">
                    <div className="ka-legend-item">
                      <span
                        className="ka-legend-dot"
                        style={{ backgroundColor: "#0f766e" }}
                      />
                      Gateways: {deviceCounts.gateways ?? 0}
                    </div>
                    <div className="ka-legend-item">
                      <span
                        className="ka-legend-dot"
                        style={{ backgroundColor: "#38bdf8" }}
                      />
                      Watches: {deviceCounts.watches ?? 0}
                    </div>
                    <div className="ka-legend-item">
                      <span
                        className="ka-legend-dot"
                        style={{ backgroundColor: "#6366f1" }}
                      />
                      NOAH: {deviceCounts.noah ?? 0}
                    </div>
                    <div className="ka-legend-item">
                      <span
                        className="ka-legend-dot"
                        style={{ backgroundColor: "#9ca3af" }}
                      />
                      Total: {deviceCounts.total ?? 0}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* SECTION 2 – Health metrics */}
          <section className="ka-section">
            <h2 className="ka-section-title">Health metrics (watches)</h2>
            <div className="ka-card-row">
              {/* Steps */}
              <div className="ka-card">
                <div className="ka-card-header">
                  <span className="ka-card-title">Steps (avg/min/max)</span>
                  <span className="ka-chip">Watches</span>
                </div>
                <div className="ka-chart">
                  {stepsChart ? (
                    <Bar data={stepsChart.data} options={stepsChart.options} />
                  ) : (
                    <div className="ka-empty">No steps data.</div>
                  )}
                </div>
              </div>

              {/* Heart rate */}
              <div className="ka-card">
                <div className="ka-card-header">
                  <span className="ka-card-title">
                    Heart rate (avg/min/max)
                  </span>
                  <span className="ka-chip">bpm</span>
                </div>
                <div className="ka-chart">
                  {heartChart ? (
                    <Bar data={heartChart.data} options={heartChart.options} />
                  ) : (
                    <div className="ka-empty">No heart rate data.</div>
                  )}
                </div>
                {heartRateStats && (
                  <div className="ka-flags-row">
                    <span className="ka-flag-pill">
                      High HR &gt; {heartRateStats.highHeartRateThreshold} bpm:{" "}
                      <strong>{heartRateStats.highHeartRateCount}</strong> watches
                    </span>
                  </div>
                )}
              </div>

              {/* Oxymetry */}
              <div className="ka-card">
                <div className="ka-card-header">
                  <span className="ka-card-title">
                    Oxymetry (avg/min/max)
                  </span>
                  <span className="ka-chip">SpO₂ %</span>
                </div>
                <div className="ka-chart">
                  {oxymChart ? (
                    <Bar data={oxymChart.data} options={oxymChart.options} />
                  ) : (
                    <div className="ka-empty">No oxymetry data.</div>
                  )}
                </div>
                {oxymetryStats && (
                  <div className="ka-flags-row">
                    <span className="ka-flag-pill">
                      Low SpO₂ &lt; {oxymetryStats.lowSpO2Threshold}%:{" "}
                      <strong>{oxymetryStats.lowSpO2Count}</strong> watches
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* SECTION 3 – Battery */}
          <section className="ka-section">
            <h2 className="ka-section-title">Battery status (watches)</h2>
            <div className="ka-card-row">
              {/* Avg / min / max battery */}
              <div className="ka-card">
                <div className="ka-card-header">
                  <span className="ka-card-title">
                    Battery level (avg/min/max)
                  </span>
                  <span className="ka-chip">%</span>
                </div>
                <div className="ka-chart">
                  {batteryChart ? (
                    <Bar
                      data={batteryChart.data}
                      options={batteryChart.options}
                    />
                  ) : (
                    <div className="ka-empty">No battery data.</div>
                  )}
                </div>
              </div>

              {/* Distribution Low / Medium / High */}
              <div className="ka-card">
                <div className="ka-card-header">
                  <span className="ka-card-title">
                    Battery distribution (watches)
                  </span>
                  <span className="ka-chip">Counts</span>
                </div>
                <div className="ka-chart">
                  {batteryDistChart ? (
                    <Bar
                      data={batteryDistChart.data}
                      options={batteryDistChart.options}
                    />
                  ) : (
                    <div className="ka-empty">No distribution data.</div>
                  )}
                </div>
                {batteryStats && (
                  <div className="ka-flags-row">
                    <span className="ka-flag-pill">
                      Low (&lt; {batteryStats.lowBatteryThreshold}%):{" "}
                      <strong>{batteryStats.lowBatteryCount}</strong>
                    </span>
                    <span className="ka-flag-pill">
                      Medium (
                      {batteryStats.lowBatteryThreshold}–
                      {batteryStats.midBatteryThreshold}%):{" "}
                      <strong>{batteryStats.midBatteryCount}</strong>
                    </span>
                    <span className="ka-flag-pill">
                      High (&gt; {batteryStats.midBatteryThreshold}%):{" "}
                      <strong>{batteryStats.highBatteryCount}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div></>
  );
}
